import { getAuth, onAuthChange } from './auth.js';
import { supabaseBrowser } from './supabaseClient.js';

/**
 * Profile state — the user's row in public.profiles, fetched once per
 * sign-in and cached. Wired up via initProfile() at boot: an auth
 * listener auto-fetches the profile whenever a session arrives, and
 * clears it on sign-out.
 *
 * Four-state cache (not three) because UI code has to distinguish
 * "we don't know yet" from "we asked and there's no row" — the former
 * shouldn't trigger the UsernameSetupScene route, only the latter
 * should:
 *
 *   - 'unloaded' — initProfile hasn't run, or the user is signed out
 *   - 'loading'  — fetch is in flight
 *   - 'missing'  — fetched, no row exists (= "go set up a username")
 *   - 'exists'   — fetched, row is here
 *
 * Server-side enforcement of regex + uniqueness lives in
 * 0005_profiles.sql; this module is purely the client cache + the
 * client-side ergonomics around it.
 */

export interface Profile {
  readonly id: string;
  readonly username: string;
  readonly email: string;
  readonly createdAt: string;
  /** Whether the user wants to receive non-mandatory email
   *  notifications (game-started, game-ended, turn reminder).
   *  Defaults true at the DB level; account-deletion receipts and
   *  Supabase auth-flow confirmations bypass this flag entirely. */
  readonly emailNotifications: boolean;
}

export type ProfileState =
  | { readonly kind: 'unloaded' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'missing' }
  | { readonly kind: 'exists'; readonly profile: Profile };

const UNLOADED: ProfileState = { kind: 'unloaded' };

let cachedState: ProfileState = UNLOADED;
let initialized = false;
const listeners = new Set<(state: ProfileState) => void>();

function setState(next: ProfileState): void {
  cachedState = next;
  for (const fn of listeners) {
    try {
      fn(cachedState);
    } catch (e) {
      console.error('profile listener threw', e);
    }
  }
}

/**
 * Wire the profile cache to auth. Call once from main.ts after
 * initAuth() — order matters because we read getAuth() synchronously
 * at the bottom of this function to seed the initial fetch.
 */
export function initProfile(): void {
  if (initialized) return;
  initialized = true;

  onAuthChange((auth) => {
    if (auth.user) {
      void fetchProfile(auth.user.id);
    } else {
      // Signed out — clear so the next sign-in starts from a clean slate.
      setState(UNLOADED);
    }
  });

  // initAuth() may have already resolved synchronously by the time
  // we get here. If so, kick off the initial fetch.
  const currentAuth = getAuth();
  if (currentAuth.user) {
    void fetchProfile(currentAuth.user.id);
  }
}

async function fetchProfile(userId: string): Promise<void> {
  setState({ kind: 'loading' });
  const supabase = supabaseBrowser();
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, email, created_at, email_notifications')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    // Don't poison the state with 'missing' on a network/permission
    // error — we genuinely don't know whether a row exists. Log and
    // bail back to 'unloaded' so a subsequent auth tick can retry.
    console.warn('profile fetch failed', error);
    setState(UNLOADED);
    return;
  }
  if (!data) {
    setState({ kind: 'missing' });
    return;
  }
  setState({
    kind: 'exists',
    profile: {
      id: data.id,
      username: data.username,
      email: data.email,
      createdAt: data.created_at,
      // The column defaults to true at the DB level, but a row
      // written before migration 0009 could read back as null/undef.
      // Default-true here mirrors the DB intent.
      emailNotifications: data.email_notifications !== false,
    },
  });
}

export function getProfile(): ProfileState {
  return cachedState;
}

export function onProfileChange(
  fn: (state: ProfileState) => void
): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// Same regex enforced by the DB check constraint in 0005_profiles.sql.
// Mirroring it client-side lets us reject obviously-bad input before
// the network round-trip, but the DB stays the source of truth.
export const USERNAME_REGEX = /^[A-Za-z0-9_.-]{3,20}$/;

/**
 * Reserved usernames — mirror of `public.is_username_allowed()` in
 * migration 0013. Case-insensitive. Updates here MUST also update the
 * SQL function (and vice versa) or the two checks will drift —
 * client-side would accept a name the DB rejects, manifesting as a
 * 23514 check-violation that the user sees as a generic regex error.
 *
 * Deliberately NOT exported as the source of truth — the DB is.
 * Exported here only so the regression test can assert the two lists
 * stay in sync.
 */
export const RESERVED_USERNAMES: ReadonlySet<string> = new Set([
  'admin',
  'administrator',
  'support',
  'system',
  'moderator',
  'mod',
  'root',
  'official',
  'staff',
  'battleshark',
  'yeenon',
]);

export function isReservedUsername(name: string): boolean {
  return RESERVED_USERNAMES.has(name.toLowerCase());
}

export type CreateProfileResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: string };

/**
 * Create the profile row for the currently signed-in user. Called
 * once from UsernameSetupScene's submit handler. On success, the
 * cached state flips to 'exists' (via the post-insert refetch).
 *
 * Distinguishes the "username already taken" case from generic
 * failures so the UI can surface a targeted error. Postgres unique
 * violations return code '23505' which Supabase passes through.
 */
export async function createProfile(
  username: string
): Promise<CreateProfileResult> {
  const auth = getAuth();
  if (!auth.user) {
    return { ok: false, error: 'You need to sign in first.' };
  }
  if (!USERNAME_REGEX.test(username)) {
    return {
      ok: false,
      error: 'Username must be 3–20 characters: letters, numbers, _ - or .',
    };
  }
  // Reserved-name pre-check mirrors migration 0013's CHECK constraint.
  // Without this the user would get a generic 23514 error from the
  // DB; with it they get a specific "reserved" message before the
  // round-trip.
  if (isReservedUsername(username)) {
    return {
      ok: false,
      error: 'That username is reserved. Pick a different one.',
    };
  }

  const supabase = supabaseBrowser();
  const { error } = await supabase.from('profiles').insert({
    id: auth.user.id,
    username,
    email: auth.user.email ?? '',
  });

  if (error) {
    // 23505 = unique violation. The case-insensitive index on
    // lower(username) is the only unique constraint on the table,
    // so a 23505 here is always "username taken."
    if (error.code === '23505') {
      return { ok: false, error: 'That username is already taken.' };
    }
    // 23514 = check violation. Two constraints can fire this:
    // (a) the regex constraint from 0005 — should be caught
    //     client-side first
    // (b) the reserved-names constraint from 0013 — should be caught
    //     client-side first
    // Either way the user-visible message is the same: bad input.
    // Defense in depth — client missing a check (e.g., race with
    // a server-side update to the reserved list) still produces a
    // usable error.
    if (error.code === '23514') {
      return {
        ok: false,
        error: "That username isn't allowed. Try a different one.",
      };
    }
    console.warn('createProfile insert failed', error);
    return { ok: false, error: error.message };
  }

  // Re-fetch to populate the cache. Listeners observe the flip from
  // 'loading' / 'missing' → 'exists' without re-running the insert.
  await fetchProfile(auth.user.id);
  return { ok: true };
}

export type UpdateEmailNotificationsResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: string };

/**
 * Flip the user's email-notifications preference. Writes directly to
 * profiles via the user's own session — RLS policy `profiles_update_self`
 * (migration 0005) gates the update to the row matching auth.uid().
 *
 * Optimistically updates the cached state on success so the Settings
 * toggle reflects the change immediately. On failure, the cache is
 * untouched and the caller should revert the UI.
 */
export async function updateEmailNotifications(
  value: boolean
): Promise<UpdateEmailNotificationsResult> {
  const auth = getAuth();
  if (!auth.user) {
    return { ok: false, error: 'You need to sign in first.' };
  }

  const supabase = supabaseBrowser();
  const { error } = await supabase
    .from('profiles')
    .update({ email_notifications: value })
    .eq('id', auth.user.id);

  if (error) {
    console.warn('updateEmailNotifications failed', error);
    return { ok: false, error: error.message };
  }

  // Mutate the cached profile in place. Simpler than a full refetch
  // and the only field that changes is the one we just wrote.
  if (cachedState.kind === 'exists') {
    setState({
      kind: 'exists',
      profile: {
        ...cachedState.profile,
        emailNotifications: value,
      },
    });
  }
  return { ok: true };
}
